import { Link, useRouterState } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Button, H3, Text, XStack, YStack } from 'tamagui';
import { ProfileMenu } from '../components/profile-menu';

type NavItem = { label: string; to: string };

const MAIN_NAV_ITEMS: readonly NavItem[] = [
  { label: 'Heute', to: '/heute' },
  { label: 'Einkaufen', to: '/einkaufen' },
  { label: 'Todo', to: '/todo' },
  { label: 'Listen', to: '/lists' },
  { label: 'Mail', to: '/mail' },
  { label: 'Archiv', to: '/archiv' },
  { label: 'Pakete', to: '/pakete' },
  { label: 'Roadmap', to: '/roadmap' },
];

const SETTINGS_NAV_ITEMS: readonly NavItem[] = [
  { label: '← Zurück', to: '/' },
  { label: 'Mailkonten', to: '/einstellungen/mail' },
  { label: 'Mail-Tags', to: '/einstellungen/tags' },
  { label: 'Geschäfte', to: '/einstellungen/geschaefte' },
  { label: 'Kalender', to: '/einstellungen/kalender' },
  { label: 'Routinen', to: '/einstellungen/habits' },
  { label: 'Projekte', to: '/einstellungen/projekte' },
  { label: 'OCR-Training', to: '/einstellungen/training' },
];

const activeLinkStyle = {
  textDecoration: 'none',
  color: 'inherit',
  fontWeight: '700',
  background: 'var(--color4)',
  borderRadius: 8,
} as const;

const linkStyle = {
  textDecoration: 'none',
  color: 'inherit',
} as const;

export function Layout({ children }: { children: ReactNode }) {
  const [navOpen, setNavOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const inSettings = pathname.startsWith('/einstellungen');
  const navItems = inSettings ? SETTINGS_NAV_ITEMS : MAIN_NAV_ITEMS;

  return (
    <YStack flex={1} minHeight="100vh" backgroundColor="$background">
      <XStack
        alignItems="center"
        gap="$2"
        paddingHorizontal="$4"
        paddingVertical="$3"
        borderBottomWidth={1}
        borderBottomColor="$borderColor"
      >
        <Button
          size="$3"
          chromeless
          display="flex"
          $gtSm={{ display: 'none' }}
          onPress={() => setNavOpen(true)}
          aria-label="Bereichs-Nav öffnen"
        >
          ☰
        </Button>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <H3>mrrmlab</H3>
        </Link>
        <XStack flex={1} />
        <ProfileMenu />
      </XStack>
      <XStack flex={1}>
        <YStack
          display="none"
          $gtSm={{ display: 'flex' }}
          width={240}
          borderRightWidth={1}
          borderRightColor="$borderColor"
          paddingTop="$5"
          paddingHorizontal="$3"
          gap="$1"
        >
          <NavItems items={navItems} vertical />
        </YStack>
        <YStack flex={1} overflow="scroll">
          {children}
        </YStack>
      </XStack>

      {navOpen ? (
        <>
          <YStack
            position={'fixed' as 'absolute'}
            top={0}
            left={0}
            right={0}
            bottom={0}
            backgroundColor="rgba(0,0,0,0.45)"
            onPress={() => setNavOpen(false)}
            zIndex={1000}
            aria-label="Bereichs-Nav schließen"
            $gtSm={{ display: 'none' }}
          />
          <YStack
            data-testid="bereichs-nav-drawer"
            position={'fixed' as 'absolute'}
            top={0}
            left={0}
            bottom={0}
            width={300}
            maxWidth="85vw"
            backgroundColor="$background"
            borderRightWidth={1}
            borderRightColor="$borderColor"
            paddingTop="$5"
            paddingHorizontal="$3"
            gap="$1"
            zIndex={1001}
            $gtSm={{ display: 'none' }}
            onPress={() => setNavOpen(false)}
          >
            <NavItems items={navItems} vertical />
          </YStack>
        </>
      ) : null}
    </YStack>
  );
}

function NavItems({ items, vertical }: { items: readonly NavItem[]; vertical: boolean }) {
  return (
    <>
      {items.map(({ label, to }) => (
        <Link
          key={to}
          to={to}
          style={linkStyle}
          activeProps={{ style: activeLinkStyle }}
        >
          <YStack
            paddingVertical={vertical ? '$3' : '$2'}
            paddingHorizontal="$3"
            borderRadius="$3"
            hoverStyle={{ backgroundColor: '$color3' }}
          >
            <Text fontSize="$4">{label}</Text>
          </YStack>
        </Link>
      ))}
    </>
  );
}
